import React, { memo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Box, Check, ChevronDown, Copy } from "lucide-react";
import {
  formatDate,
  ICourier,
  InvoiceType,
  IOrder,
  OrderDeliveryType,
  OrderStatus,
  priceFormat,
  SellerFulfillmentType,
} from "muminsmart-utils";
import CustomImage from "@/app/components/common/Image";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { homeDivider } from "@/assets";
import { routes } from "@/lib/constants";
import { inter, philosopher } from "@/lib/fonts";
import { usePrefetchProduct } from "@/lib/hooks/usePrefetchProduct";
import { cn } from "@/lib/utils";

import TrackingForm from "./TrackingForm";

function isSelfFulfilledOrder(order: IOrder): boolean {
  return (
    (order.fulfillmentType ?? SellerFulfillmentType.Self) ===
    SellerFulfillmentType.Self
  );
}

const orderStatusColors: Record<OrderStatus, string> = {
  [OrderStatus.Pending]: "#F2994A",
  [OrderStatus.Processing]: "#F2994A",
  [OrderStatus.Dispatched]: "#F2994A",
  [OrderStatus.Shipped]: "#C8892B",
  [OrderStatus.Delivered]: "#1F9975",
  [OrderStatus.Cancelled]: "#6F6F6F",
  [OrderStatus.Returned]: "#6F6F6F",
  [OrderStatus.Disputed]: "#FF0000",
  [OrderStatus.Refunded]: "#4c6cbf",
};

export interface OrderCardProps {
  order: IOrder;
  index: number;
  isSeller: boolean;
  isExpanded: boolean;
  couriers: ICourier[];
  copiedOrderId: string | null;
  updatingOrderStatusId: string | null;
  // eslint-disable-next-line no-unused-vars
  onToggleExpand: (orderId: string) => void;
  // eslint-disable-next-line no-unused-vars
  onCopyAddress: (orderId: string, address: string) => void;
  // eslint-disable-next-line no-unused-vars
  onUpdateStatus: (orderId: string, status: OrderStatus) => void;
  // eslint-disable-next-line no-unused-vars
  onOrderUpdated: (order: IOrder) => void;
}

type OrderLineItemProps = {
  item: IOrder["items"][number];
  isSeller: boolean;
};

const OrderLineItem = memo(function OrderLineItem({
  item,
  isSeller,
}: OrderLineItemProps) {
  const navigate = useNavigate();
  const prefetchProduct = usePrefetchProduct();
  const variant = item.productVariant;
  const imageKey = variant?.featuredImage;
  const variantName = variant?.variantAttributes
    ?.map((attribute) => attribute.value)
    .filter(Boolean)
    .join(" / ");
  const productId = variant?.product?.id ?? "";

  const handleNavigate = useCallback(() => {
    navigate(routes.products.id(productId, variant?.id));
  }, [productId, navigate, variant?.id]);

  return (
    <div
      key={variant?.id || item.id}
      className="grid grid-cols-[auto_1fr] gap-3 rounded-3xl bg-[#F8F8F8] p-2 sm:p-3"
    >
      <div className="flex justify-center">
        <div
          role="button"
          tabIndex={0}
          className="flex h-18 w-18 items-center justify-center rounded-lg sm:h-20 sm:w-20"
          onClick={handleNavigate}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleNavigate();
            }
          }}
          onMouseEnter={() => prefetchProduct(productId)}
          onFocus={() => prefetchProduct(productId)}
        >
          <CustomImage
            src={imageKey ?? ""}
            alt={variant?.product?.name ?? "Product Image"}
            className="h-18 w-18 rounded-lg object-cover sm:h-20 sm:w-20"
          />
        </div>
      </div>
      <div className="min-w-0 space-y-1">
        <div className="min-w-0">
          <h3
            className={`line-clamp-2 text-sm font-bold leading-snug text-gray-800 ${philosopher.className}`}
          >
            {variant?.product?.name || "Product Name"}{" "}
            <span
              className={cn(
                "rounded-lg bg-secondary p-1 font-bold text-white",
                inter.className,
              )}
            >
              {item.quantity} pc
            </span>
          </h3>

          {variant?.product?.isMuminsChoice ? (
            <p className="rounded-lg text-[0.6rem] font-semibold text-primary">
              MUMIN&apos;S CHOICE
            </p>
          ) : null}

          <p className="text-wrap text-xs font-medium text-gray-800">
            {variantName}
          </p>

          <p className="mt-1 text-xs text-gray-500">
            {priceFormat(Number(item.unitPrice))} x {item.quantity} ={" "}
            <span className="font-semibold text-gray-900">
              {priceFormat(Number(item.totalPrice))}
            </span>
          </p>
        </div>
        {isSeller && variant?.product?.location ? (
          <div className="flex justify-start">
            <p className="rounded-full bg-primary px-2 py-1 text-xs text-white">
              {variant.product.location}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
});

function OrderCard({
  order,
  index,
  isSeller,
  isExpanded,
  couriers,
  copiedOrderId,
  updatingOrderStatusId,
  onToggleExpand,
  onCopyAddress,
  onUpdateStatus,
  onOrderUpdated,
}: OrderCardProps) {
  const color = orderStatusColors[order.status];
  const canSellerManageShipment = isSeller && isSelfFulfilledOrder(order);
  const customerInvoice = order.invoices?.find(
    (invoice) => invoice.type === InvoiceType.VendorToCustomer,
  );

  return (
    <div className="space-y-2">
      {index > 0 ? (
        <img
          src={homeDivider}
          alt="Group"
          className="h-auto w-full max-w-2xl mx-auto px-4 py-3"
        />
      ) : null}
      <div className="space-y-2 rounded-3xl border border-gray-300 p-2">
        <div className="flex items-center gap-2" style={{ color }}>
          <div
            className="flex items-center justify-center rounded-full border p-1.5"
            style={{ border: `1px solid ${color}` }}
          >
            <Box className="h-4 w-4" />
          </div>
          <div>
            <p className="text-sm">{order.status}</p>
            <p className="text-xs text-muted">{order.orderNumber}</p>
            <div className="flex flex-col gap-1">
              {isSeller &&
              ((order.status === OrderStatus.Shipped &&
                order.courierName &&
                order.trackingNumber) ||
                order.status === OrderStatus.Delivered)
                ? order.invoices?.map((invoice, invoiceIndex) => (
                    <Link
                      to={routes.invoices.id(invoice.id)}
                      key={invoice.id}
                      className="mt-1 text-xs text-primary underline"
                    >
                      View Invoice {invoiceIndex + 1}
                    </Link>
                  ))
                : null}
              {!isSeller && customerInvoice ? (
                <Link
                  to={routes.invoices.id(customerInvoice.id)}
                  className="mt-1 text-xs text-primary underline"
                >
                  View Invoice
                </Link>
              ) : null}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2">
          <div>
            <div className={`pl-3 text-sm text-gray-700 ${inter.className}`}>
              <strong>Ordered By:</strong> {order.user?.fullName || "N/A"}
            </div>
            <div className={`pl-3 text-sm text-gray-700 ${inter.className}`}>
              <strong>Phone:</strong>{" "}
              <a href={`tel:${order.user?.phoneNumber || ""}`}>
                {order.user?.phoneNumber || "N/A"}
              </a>
            </div>
            <div className={`pl-3 text-sm text-gray-700 ${inter.className}`}>
              <strong>Delivery Type:</strong> {order.deliveryType}
            </div>
            <div className={`pl-3 text-sm text-gray-700 ${inter.className}`}>
              <strong>Order Date:</strong> {formatDate(order.createdAt)}
            </div>
            {order.remark ? (
              <div
                className={`pl-3 pt-2 text-sm text-gray-700 ${inter.className}`}
              >
                <strong className="text-primary">Remark:</strong> {order.remark}
              </div>
            ) : null}
            {order.deliveryType === OrderDeliveryType.Courier &&
            order.address?.preferredCourier ? (
              <div className={`pl-3 text-sm text-gray-700 ${inter.className}`}>
                <strong>Preferred Courier:</strong>{" "}
                {order.address.preferredCourier}
              </div>
            ) : null}
            {order.deliveryType === OrderDeliveryType.Transport &&
            order.address?.preferredTransport ? (
              <div className={`pl-3 text-sm text-gray-700 ${inter.className}`}>
                <strong>Preferred Transport:</strong>{" "}
                {order.address.preferredTransport}
              </div>
            ) : null}
          </div>
          <div className="flex items-center justify-end gap-2">
            {canSellerManageShipment ? (
              <Select
                value={order.status}
                disabled={updatingOrderStatusId === order.id}
                onValueChange={(value: OrderStatus) => {
                  onUpdateStatus(order.id, value);
                }}
              >
                <SelectTrigger className="rounded-full border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(OrderStatus).map((statusOption) => (
                    <SelectItem key={statusOption} value={statusOption}>
                      {statusOption}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
            {isSeller && !canSellerManageShipment ? (
              <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-600">
                Platform fulfilled
              </span>
            ) : null}

            {!isSeller && order.status === OrderStatus.Shipped ? (
              <button
                type="button"
                disabled={updatingOrderStatusId === order.id}
                className="rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60"
                onClick={() => onUpdateStatus(order.id, OrderStatus.Delivered)}
              >
                Mark as Received
              </button>
            ) : null}

            <button
              type="button"
              aria-label={
                isExpanded ? "Hide order details" : "View order details"
              }
              className="rounded-full border border-gray-200 bg-white p-1.5 text-gray-600"
              onClick={() => onToggleExpand(order.id)}
            >
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-300 ${
                  isExpanded ? "rotate-180" : "rotate-0"
                }`}
              />
            </button>
          </div>
        </div>
        <div
          className={`grid transition-[grid-template-rows,opacity,margin] duration-300 ease-in-out ${
            isExpanded
              ? "mt-1 grid-rows-[1fr] opacity-100"
              : "grid-rows-[0fr] opacity-0"
          }`}
        >
          <div className="space-y-2 overflow-hidden">
            <div className="rounded-3xl border border-gray-200 bg-white p-3">
              <div className="flex items-start justify-between gap-1">
                <p className="text-sm font-semibold text-gray-900">
                  Shipping Details
                </p>
                {isSeller ? (
                  <button
                    type="button"
                    aria-label="Copy address"
                    className="ml-1.5 inline-flex align-middle text-secondary"
                    onClick={() =>
                      onCopyAddress(
                        order.id,
                        [
                          order.shippingName,
                          `+${order.shippingPhone}`,
                          [
                            order.shippingAddressLine1,
                            order.shippingAddressLine2,
                            `${order.shippingCity}, ${order.shippingState} - ${order.shippingPincode}`,
                          ]
                            .filter(Boolean)
                            .join(", "),
                        ]
                          .filter(Boolean)
                          .join("\n"),
                      )
                    }
                  >
                    {copiedOrderId === order.id ? (
                      <Check className="h-3.5 w-3.5 text-primary" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                  </button>
                ) : null}
              </div>

              <div className="mb-2 mt-2 space-y-1 text-xs text-gray-700">
                <p>
                  <span className="font-medium">Name:</span>{" "}
                  {order.shippingName}
                </p>
                <p>
                  <span className="font-medium">Phone:</span> +
                  {order.shippingPhone}
                </p>
                <p>
                  <span className="font-medium">Address:</span>{" "}
                  {[
                    order.shippingAddressLine1,
                    order.shippingAddressLine2,
                    order.shippingCity,
                    order.shippingState,
                    order.shippingPincode,
                    order.shippingCountry,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </p>
              </div>

              {canSellerManageShipment &&
              order.status === OrderStatus.Shipped ? (
                <TrackingForm
                  couriers={couriers}
                  order={order}
                  onUpdated={onOrderUpdated}
                />
              ) : null}
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-3">
              <p className="text-sm font-semibold text-gray-900">
                Order Summary
              </p>
              <div className="mt-2 space-y-1 text-xs text-gray-700">
                <p>
                  <span className="font-medium">Subtotal:</span>{" "}
                  {priceFormat(order.subtotal)}
                </p>
                <p>
                  <span className="font-medium">Coupon Discount:</span> -
                  {priceFormat(order.couponDiscount)}
                </p>
                <p>
                  <span className="font-medium">Shipping Charge:</span>{" "}
                  {priceFormat(order.shippingCharge)}
                </p>
                <p className="pt-1 text-sm font-semibold text-gray-900">
                  Total: {priceFormat(order.totalAmount)}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {order.items.map((item) => (
            <OrderLineItem
              key={variantKey(item)}
              item={item}
              isSeller={isSeller}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function variantKey(item: IOrder["items"][number]) {
  return item.productVariant?.id || item.id;
}

export default memo(OrderCard);
